import React, { useState, useEffect } from 'react';
import { DollarSign } from 'lucide-react';
import { CLOSET } from '../data/closet';
import { useSolPrice } from '../contexts/SolPriceContext';
import CustomIcon from './CustomIcon';

const OutfitPreview = ({ outfit, className = '' }) => {
  const { solPrice } = useSolPrice(); // Get real-time SOL price from global context
  
  if (!outfit) return null;

  const { models = [], category, pieces } = outfit;

  // Helper function to get price from model or closet data
  const getItemPrice = (model) => {
    // First try to use the price from the model itself
    if (model.price && parseFloat(model.price) > 0) {
      return parseFloat(model.price);
    }
    
    // If no price in model, try to find it in the closet data by ID
    if (model.id) {
      const closetItem = CLOSET.find(item => item.id === model.id);
      if (closetItem && closetItem.price) {
        return parseFloat(closetItem.price);
      }
    }
    
    // If still no price found, try to match by name and category
    const closetItem = CLOSET.find(item => 
      item.name === model.name && item.category === model.category
    );
    if (closetItem && closetItem.price) {
      return parseFloat(closetItem.price);
    }
    
    return 0;
  };

  const totalPriceSOL = models.reduce((sum, model) => sum + getItemPrice(model), 0);
  const totalPriceUSD = totalPriceSOL * solPrice;


  const getCategoryColor = (category) => {
    switch (category?.toLowerCase()) {
      case 'tops':
        return 'text-blue-400';
      case 'bottoms':
        return 'text-green-400';
      case 'shoes':
        return 'text-yellow-400';
      case 'hats':
        return 'text-purple-400';
      default:
        return 'text-gray-400';
    }
  };

  return (
    <div className={`relative w-full h-full bg-[#1a1a1a] flex flex-col ${className}`}>

      {/* Main Content */}
      <div className="flex flex-col h-full p-3 pt-10">
        {/* Outfit Title */}
        <div className="text-center mb-2 flex-shrink-0">
          <h3 className="text-white font-semibold text-sm mb-1 truncate max-w-full">
            {outfit.name || 'Untitled Outfit'}
          </h3>
          <p className="text-gray-400 text-xs">
            {pieces || models.length} piece{pieces !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Items List - Compact Grid Layout with Scroll */}
        <div className="flex-1 overflow-y-auto mb-2 pr-1 min-h-0">
          <div className="grid grid-cols-1 gap-1">
            {models.map((model, index) => (
              <div
                key={model.id || index}
                className="flex items-center justify-between p-1.5 bg-gray-700/20 rounded flex-shrink-0"
              >
                <div className="flex items-center gap-1.5 flex-1 min-w-0">
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-xs font-medium truncate">
                      {model.name || `${model.category} ${index + 1}`}
                    </p>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-green-400 text-xs font-medium whitespace-nowrap">
                    {getItemPrice(model).toFixed(2)} SOL
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Total Price */}
        <div className="pt-2 border-t border-gray-600 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              <DollarSign size={12} className="text-green-400" />
              <span className="text-white text-xs font-semibold">Total</span>
            </div>
            <div className="text-right flex-shrink-0">
              <div className="text-green-400 text-xs font-bold whitespace-nowrap">
                {totalPriceSOL.toFixed(2)} SOL
              </div>
              <div className="text-xs text-gray-500 whitespace-nowrap">
                ≈ ${totalPriceUSD.toFixed(2)} USD
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OutfitPreview;
